import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './components/common/login';
import DashboardContent from './components/dashboard/dashboardcontent';
import PatientTrendsDashboard from './components/dashboard/ui/patientTrendsDashboard';
import PatientEcgDashboard from './components/dashboard/ui/patientEcgDashboard';

import EventLogsContent from './components/eventLogs/eventLogsContent';

import RaiseIssue from './components/issues/addIssue';
import UnderConstructionLogs from './components/common/underConstruction';
import UnderConstructionReports from './components/common/underConstruction';
import AllIssueContent from './components/issues/allIssueContent';
import AllDeviceContent from './components/devices/allDeviceContent';
import OnboardedPatientsContent from './components/onboarded/patientContent';
import OnboardedEditPatientPage from './components/onboarded/editPatient';
import OnboardedAddPatientPage from './components/onboarded/addPatient';
import OnboardedPatientDetails from './components/onboarded/patient';
import DischargedPatientDetails from './components/discharge/dischargedPatient';
import HospitalContent from './components/hospitals/hospitalContent';
import EditHospitalPage from './components/hospitals/editHospital';
import AddHospitalPage from './components/hospitals/addHospital';
import HospitalDetails from './components/hospitals/hospital';
import DevicesContent from './components/devices/deviceContent';
import EditDevicePage from './components/devices/editDevice';
import AddDevicePage from './components/devices/addDevice';
import DeviceDetails from './components/devices/device';
import UserContent from './components/users/userContent';
import EditUserPage from './components/users/editUser';
import AddUserPage from './components/users/addUser';
import UserDetails from './components/users/user';
import DoctorContent from './components/doctors/doctorContent';
import EditDoctorPage from './components/doctors/editDoctor';
import AddDoctorPage from './components/doctors/addDoctor';
import DoctorDetails from './components/doctors/doctor';
import DischargeContent from './components/discharge/dischargecontent';
import ChangePassword from './components/common/changePassword'; // Import ChangePassword component
import { Helmet } from 'react-helmet';

const Sidebar = lazy(() => import('./components/common/sidebar'));

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [isFirstLogin, setIsFirstLogin] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        const firstLogin = localStorage.getItem('is_first_login') === 'true'; // Check is_first_login
        setIsAuthenticated(!!token);
        setIsFirstLogin(firstLogin);
      } catch (error) {
        console.error('Error accessing localStorage:', error);
        setIsAuthenticated(false);
        setIsFirstLogin(false);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();

    window.addEventListener('storage', checkAuth);

    return () => {
      window.removeEventListener('storage', checkAuth);
    };
  }, []);

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <Router>
      <div className="flex h-screen bg-gray-100">
        <Helmet>
          <title>Riqaba Innovations</title>
        </Helmet>

        {isAuthenticated && !isFirstLogin ? (
          <Suspense fallback={<div>Loading Sidebar...</div>}>
            <Sidebar />
          </Suspense>
        ) : null}

        <div className="flex-1 overflow-y-auto">
          <Routes>
            {/* Login Route */}
            <Route
              path="/login"
              element={isAuthenticated ? <Navigate to={isFirstLogin ? '/changePassword' : '/dashboard'} replace /> : <Login />}
            />

            {/* Change Password Route */}
            <Route
              path="/changePassword"
              element={
                isAuthenticated ? (
                  isFirstLogin ? (
                    <ChangePassword />
                  ) : (
                    <Navigate to="/dashboard" replace />
                  )
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />

            {/* Protected Routes (only accessible if not first login) */}
            <Route
              path="/dashboard"
              element={
                isAuthenticated ? (
                  isFirstLogin ? (
                    <Navigate to="/changePassword" replace />
                  ) : (
                    <DashboardContent />
                  )
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />

            <Route
              path="/dashboard/trends/:hospId/:patientId/:bedId"
              element={
                isAuthenticated ? (
                  isFirstLogin ? (
                    <Navigate to="/changePassword" replace />
                  ) : (
                    <PatientTrendsDashboard />
                  )
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />

<Route
              path="/dashboard/ecg/:hospId/:patientId/:bedId"
              element={
                isAuthenticated ? (
                  isFirstLogin ? (
                    <Navigate to="/changePassword" replace />
                  ) : (
                    <PatientEcgDashboard />
                  )
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />

            <Route
              path="/raiseIssue"
              element={
                isAuthenticated ? (
                  isFirstLogin ? (
                    <Navigate to="/changePassword" replace />
                  ) : (
                    <RaiseIssue />
                  )
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/underConstructionReports"
              element={
                isAuthenticated ? (
                  isFirstLogin ? (
                    <Navigate to="/changePassword" replace />
                  ) : (
                    <UnderConstructionReports />
                  )
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />

<Route
              path="/logs/:hospId/:patientId/:bedId"
              element={
                isAuthenticated ? (
                  isFirstLogin ? (
                    <Navigate to="/changePassword" replace />
                  ) : (
                    <EventLogsContent />
                  )
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />


            <Route
              path="/underConstructionLogs"
              element={
                isAuthenticated ? (
                  isFirstLogin ? (
                    <Navigate to="/changePassword" replace />
                  ) : (
                    <UnderConstructionLogs />
                  )
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/patients"
              element={
                isAuthenticated ? (
                  isFirstLogin ? (
                    <Navigate to="/changePassword" replace />
                  ) : (
                    <DischargeContent />
                  )
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/admin/hospitals"
              element={
                isAuthenticated ? (
                  isFirstLogin ? (
                    <Navigate to="/changePassword" replace />
                  ) : (
                    <HospitalContent />
                  )
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/admin/hospitals/add"
              element={
                isAuthenticated ? (
                  isFirstLogin ? (
                    <Navigate to="/changePassword" replace />
                  ) : (
                    <AddHospitalPage />
                  )
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/admin/hospitals/edit/:id"
              element={
                isAuthenticated ? (
                  isFirstLogin ? (
                    <Navigate to="/changePassword" replace />
                  ) : (
                    <EditHospitalPage />
                  )
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/admin/hospitals/:hospitalId"
              element={
                isAuthenticated ? (
                  isFirstLogin ? (
                    <Navigate to="/changePassword" replace />
                  ) : (
                    <HospitalDetails />
                  )
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/admin/devices"
              element={
                isAuthenticated ? (
                  isFirstLogin ? (
                    <Navigate to="/changePassword" replace />
                  ) : (
                    <DevicesContent />
                  )
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/admin/devices/add"
              element={
                isAuthenticated ? (
                  isFirstLogin ? (
                    <Navigate to="/changePassword" replace />
                  ) : (
                    <AddDevicePage />
                  )
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/admin/devices/edit/:id"
              element={
                isAuthenticated ? (
                  isFirstLogin ? (
                    <Navigate to="/changePassword" replace />
                  ) : (
                    <EditDevicePage />
                  )
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/admin/devices/:deviceId"
              element={
                isAuthenticated ? (
                  isFirstLogin ? (
                    <Navigate to="/changePassword" replace />
                  ) : (
                    <DeviceDetails />
                  )
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/onboarded/patients"
              element={
                isAuthenticated ? (
                  isFirstLogin ? (
                    <Navigate to="/changePassword" replace />
                  ) : (
                    <OnboardedPatientsContent />
                  )
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/onboard/patients/:hospitalId/add"
              element={
                isAuthenticated ? (
                  isFirstLogin ? (
                    <Navigate to="/changePassword" replace />
                  ) : (
                    <OnboardedAddPatientPage />
                  )
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/onboarded/patients/:hospitalId/edit/:id"
              element={
                isAuthenticated ? (
                  isFirstLogin ? (
                    <Navigate to="/changePassword" replace />
                  ) : (
                    <OnboardedEditPatientPage />
                  )
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/onboarded/patients/:patientId"
              element={
                isAuthenticated ? (
                  isFirstLogin ? (
                    <Navigate to="/changePassword" replace />
                  ) : (
                    <OnboardedPatientDetails />
                  )
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/discharged/patients/:patientId"
              element={
                isAuthenticated ? (
                  isFirstLogin ? (
                    <Navigate to="/changePassword" replace />
                  ) : (
                    <DischargedPatientDetails />
                  )
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />


            <Route
              path="/admin/users"
              element={
                isAuthenticated ? (
                  isFirstLogin ? (
                    <Navigate to="/changePassword" replace />
                  ) : (
                    <UserContent />
                  )
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/admin/users/add"
              element={
                isAuthenticated ? (
                  isFirstLogin ? (
                    <Navigate to="/changePassword" replace />
                  ) : (
                    <AddUserPage />
                  )
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/admin/users/edit/:id"
              element={
                isAuthenticated ? (
                  isFirstLogin ? (
                    <Navigate to="/changePassword" replace />
                  ) : (
                    <EditUserPage />
                  )
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/admin/users/:userId"
              element={
                isAuthenticated ? (
                  isFirstLogin ? (
                    <Navigate to="/changePassword" replace />
                  ) : (
                    <UserDetails />
                  )
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/admin/doctors"
              element={
                isAuthenticated ? (
                  isFirstLogin ? (
                    <Navigate to="/changePassword" replace />
                  ) : (
                    <DoctorContent />
                  )
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/admin/doctors/:hospitalId/add"
              element={
                isAuthenticated ? (
                  isFirstLogin ? (
                    <Navigate to="/changePassword" replace />
                  ) : (
                    <AddDoctorPage />
                  )
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/admin/doctors/:hospitalId/edit/:id"
              element={
                isAuthenticated ? (
                  isFirstLogin ? (
                    <Navigate to="/changePassword" replace />
                  ) : (
                    <EditDoctorPage />
                  )
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/admin/doctors/:userId"
              element={
                isAuthenticated ? (
                  isFirstLogin ? (
                    <Navigate to="/changePassword" replace />
                  ) : (
                    <DoctorDetails />
                  )
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/admin/issues"
              element={
                isAuthenticated ? (
                  isFirstLogin ? (
                    <Navigate to="/changePassword" replace />
                  ) : (
                    <AllIssueContent />
                  )
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/admin/alldevices"
              element={
                isAuthenticated ? (
                  isFirstLogin ? (
                    <Navigate to="/changePassword" replace />
                  ) : (
                    <AllDeviceContent />
                  )
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />

            {/* Catch-all Route */}
            <Route
              path="*"
              element={
                isAuthenticated ? (
                  isFirstLogin ? (
                    <Navigate to="/changePassword" replace />
                  ) : (
                    <Navigate to="/dashboard" replace />
                  )
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
          </Routes>
        </div>
      </div>
    </Router>
  );
};

export default App;